/**
 * todayV2Selectors — todo lo que Hoy V2 muestra, derivado en funciones puras.
 *
 * Reglas que se siguen aquí:
 *   1. Nada se declara dos veces. Las llegadas, las salidas, las limpiezas y
 *      el estado de cada unidad salen de las reservas y los bloqueos de la
 *      demo PMS (`pmsSelectors`), no de una lista paralela.
 *   2. El estado de una unidad se DERIVA (§8.1): "vacía y sucia" o "vacía y
 *      lista" es consecuencia de la tarea de limpieza, nunca un campo suelto.
 *   3. El vocabulario y los horarios vienen de Ajustes V2, así que cambiar
 *      "villa" por "habitación" cambia lo que dice esta pantalla.
 *
 * Sin estado propio, sin efectos, sin red.
 */
import { DEMO_TODAY_ISO, VILLAS } from "@/data/pmsDemoData";
import {
  arrivalsOn, departuresOn, housekeepingTasksForDay, getVilla, getChannel,
  reservationsForVilla, blocksForVilla, isDayInBlock, isNightInStay,
  getReservation,
} from "@/lib/pmsSelectors";
import {
  DEPARTURE_DETAILS, REQUEST_TYPE_BY_KEY,
  STAFF_BY_ID, URGENCY_ORDER, ROLE_PREVIEW_BY_KEY, DEFAULT_ROLE_PREVIEW,
} from "@/data/todayV2Demo";
import { buildGuestParty, readinessLine, asksTransfer } from "@/lib/guestsV2Selectors";
import { arrivalModeOf } from "@/data/guestsV2Demo";
import { moneyFor, servicesFor, paymentEventsFor } from "@/lib/reservationsV2Selectors";

// ---------------------------------------------------------------------------
// Utilidades
// ---------------------------------------------------------------------------

/** "lunes, 21 de agosto" — la fecha local de la propiedad (§5.1). */
export function formatTodayLabel(iso = DEMO_TODAY_ISO) {
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString("es-ES", {
    weekday: "long", day: "numeric", month: "long", timeZone: "UTC",
  });
}

/** Sustituye `{unidad}` por el vocabulario configurado en Ajustes. */
export function withUnitWord(template, unitLabel) {
  if (!template) return template;
  return template
    .replace(/\{unidad\}/g, unitLabel.singular.toLowerCase())
    .replace(/\{unidades\}/g, unitLabel.plural.toLowerCase());
}

/** Etiqueta de una petición del huésped, ya con el vocabulario aplicado. */
export function requestLabel(typeKey, unitLabel) {
  const type = REQUEST_TYPE_BY_KEY[typeKey];
  if (!type) return typeKey;
  return type.label || withUnitWord(type.label_template, unitLabel);
}

function staffName(staffId) {
  return staffId ? STAFF_BY_ID[staffId]?.name || null : null;
}

const HK_DONE = new Set(["READY", "INSPECTED"]);

/** Estado V2 de una incidencia a partir del estado del dataset V1 (§8.2). */
function issueStatusV2(issue) {
  if (issue.status === "resolved") return "RESOLVED";
  if (issue.status === "in_progress") return "IN_PROGRESS";
  if (issue.status === "assigned" || issue.assignee) return "ASSIGNED";
  return "OPEN";
}

// ---------------------------------------------------------------------------
// Limpiezas del día
// ---------------------------------------------------------------------------

const HK_PRIORITY_RANK = { urgent: 0, high: 1, medium: 2, low: 3 };

/**
 * Orden operativo: primero lo que tiene salida y llegada el mismo día (la
 * ventana es de horas), después por prioridad y por unidad. Se exporta
 * aparte para poder probar la regla sin montar la pantalla.
 */
export function sortHousekeeping(tasks) {
  return [...tasks].sort((a, b) => {
    if (a.same_day !== b.same_day) return a.same_day ? -1 : 1;
    const pa = HK_PRIORITY_RANK[a.priority] ?? 9;
    const pb = HK_PRIORITY_RANK[b.priority] ?? 9;
    if (pa !== pb) return pa - pb;
    return a.unit_id.localeCompare(b.unit_id);
  });
}

/**
 * Tareas de limpieza de hoy: las que nacen del calendario (salidas, llegadas)
 * más las que nacen de una petición del huésped. Que una petición de toallas
 * se convierta en trabajo estructurado — y no en un mensaje suelto — es el
 * punto del modelo (§7.2, §8.1).
 */
function buildHousekeeping({ iso, state, unitLabel }) {
  const seedTasks = housekeepingTasksForDay(iso).map((t) => {
    const unitId = t.villa.villa_id;
    const runtime = state.housekeeping[unitId] || {};
    const sameDay = Boolean(t.checkout_res && t.next_res);
    const type = sameDay
      ? "turnaround"
      : (t.checkout_res ? "checkout" : "arrival_check");
    return {
      task_id: `HK-${unitId}`,
      unit_id: unitId,
      unit: t.villa,
      type: runtime.deep_clean_issue_id ? "deep_clean" : type,
      same_day: sameDay,
      priority: sameDay ? "urgent" : (t.checkout_res ? "medium" : "low"),
      status: runtime.status || t.initial_status,
      assignee: staffName(runtime.assignee),
      assignee_id: runtime.assignee || null,
      next_arrival: t.next_res || null,
      checkout: t.checkout_res || null,
      issue_id: runtime.deep_clean_issue_id || null,
      from_request: null,
      advanceable: true,
    };
  });

  // `housekeepingTasksForDay` solo emite tarea cuando hay salida ese día: su
  // rama de "llega y no hay estancia" no se alcanza nunca, porque la propia
  // reserva que llega ya ocupa esa noche. V2 sí sigue el repaso previo a la
  // llegada como tarea propia, así que se añade aquí en vez de tocar el
  // selector V1, del que dependen otras pantallas.
  const covered = new Set(seedTasks.map((t) => t.unit_id));
  const arrivalTasks = arrivalsOn(iso)
    .filter((r) => !covered.has(r.assigned_villa_id))
    .map((r) => {
      const unitId = r.assigned_villa_id;
      const runtime = state.housekeeping[unitId] || {};
      return {
        task_id: `HK-${unitId}`,
        unit_id: unitId,
        unit: getVilla(unitId),
        type: runtime.deep_clean_issue_id ? "deep_clean" : "arrival_check",
        same_day: false,
        priority: "low",
        status: runtime.status || "READY",
        assignee: staffName(runtime.assignee),
        assignee_id: runtime.assignee || null,
        next_arrival: r,
        checkout: null,
        issue_id: runtime.deep_clean_issue_id || null,
        from_request: null,
        advanceable: true,
      };
    });

  const requestTasks = state.requests
    .filter((r) => {
      const type = REQUEST_TYPE_BY_KEY[r.type];
      return type && type.creates === "housekeeping_task" && r.status !== "delivered";
    })
    .map((r) => ({
      task_id: `HK-${r.request_id}`,
      unit_id: r.villa_id,
      unit: getVilla(r.villa_id),
      type: "request",
      same_day: false,
      priority: r.status === "requested" ? "medium" : "low",
      status: r.status === "in_progress" ? "IN_PROGRESS" : "PENDING",
      assignee: staffName(r.assignee),
      assignee_id: r.assignee || null,
      next_arrival: null,
      checkout: null,
      issue_id: null,
      from_request: { ...r, label: requestLabel(r.type, unitLabel) },
      advanceable: false,
    }));

  return sortHousekeeping([...seedTasks, ...arrivalTasks, ...requestTasks]);
}

// ---------------------------------------------------------------------------
// Tablero de unidades
// ---------------------------------------------------------------------------

/**
 * Estado de una unidad hoy. El orden de las reglas importa: un bloqueo de
 * mantenimiento manda sobre cualquier otra cosa porque impide vender la
 * unidad, y a partir de ahí lo que decide es la tarea de limpieza.
 */
export function unitStateOn({ unitId, iso, housekeeping, issues }) {
  const blocks = blocksForVilla(unitId).filter((b) => isDayInBlock(iso, b));
  const maintenance = blocks.find((b) => b.type === "MAINTENANCE");
  const ownerStay = blocks.find((b) => b.type === "OWNER_STAY");

  const reservations = reservationsForVilla(unitId);
  const arriving = reservations.find((r) => r.check_in === iso);
  const departing = reservations.find((r) => r.check_out === iso);
  const inStay = reservations.find((r) => isNightInStay(iso, r.check_in, r.check_out));

  const hk = housekeeping.find((t) => t.unit_id === unitId && t.advanceable) || null;
  const hkDone = hk ? HK_DONE.has(hk.status) : true;
  const hkStarted = hk ? hk.status === "IN_PROGRESS" : false;

  if (maintenance) {
    const issue = issues.find((i) => i.linked_block_id === maintenance.block_id)
      || issues.find((i) => i.villa_id === unitId && i.blocks_inventory && i.status !== "resolved")
      || null;
    return { state: "OUT_OF_SERVICE", block: maintenance, issue, arriving, departing, inStay, hk };
  }
  if (ownerStay) {
    return { state: "OWNER_STAY", block: ownerStay, issue: null, arriving, departing, inStay, hk };
  }
  if (arriving) {
    const state = hkDone ? "ARRIVING" : (hkStarted ? "CLEANING" : "VACANT_DIRTY");
    return { state, block: null, issue: null, arriving, departing, inStay, hk };
  }
  if (inStay) {
    return { state: "OCCUPIED", block: null, issue: null, arriving, departing, inStay, hk };
  }
  if (departing) {
    const state = hkDone ? "VACANT_READY" : (hkStarted ? "CLEANING" : "DEPARTING");
    return { state, block: null, issue: null, arriving, departing, inStay, hk };
  }
  return {
    state: hkDone ? "VACANT_READY" : "VACANT_DIRTY",
    block: null, issue: null, arriving, departing, inStay, hk,
  };
}

function buildBoard({ iso, housekeeping, issues, checkOutTime, etaByReservation }) {
  return VILLAS.map((unit) => {
    const row = unitStateOn({ unitId: unit.villa_id, iso, housekeeping, issues });
    return { unit, ...row, iso, detail: boardDetail({ ...row, iso }, checkOutTime, etaByReservation) };
  });
}

/** Segunda línea del azulejo: lo único que hace falta saber de un vistazo. */
function boardDetail(row, checkOutTime, etaByReservation = {}) {
  if (row.state === "OUT_OF_SERVICE") {
    return row.issue ? row.issue.title : "Bloqueada por mantenimiento";
  }
  if (row.state === "OWNER_STAY") return "Estancia del propietario";
  if (row.arriving) {
    // La misma hora que la lista de llegadas, y de la misma fuente: el azulejo
    // y la fila no pueden decir horas distintas del mismo huésped.
    const eta = etaByReservation[row.arriving.reservation_id];
    const when = eta ? `Llega ${eta}` : "Llega hoy";
    if (row.hk && !HK_DONE.has(row.hk.status)) {
      return `${when} · limpieza ${row.hk.status === "IN_PROGRESS" ? "en curso" : "pendiente"}`;
    }
    return when;
  }
  if (row.inStay) {
    return `Salida ${relativeDayLabel(row.inStay.check_out, row.iso)} · ${checkOutTime}`;
  }
  if (row.departing) return `Salió hoy · ${checkOutTime}`;
  return "Sin reserva hoy";
}

/** "hoy" / "mañana" / "el 26 de ago" — lo justo para leer un azulejo. */
function relativeDayLabel(iso, todayISO) {
  const day = 86400000;
  const a = new Date(`${todayISO}T00:00:00Z`).getTime();
  const b = new Date(`${iso}T00:00:00Z`).getTime();
  const diff = Math.round((b - a) / day);
  if (diff === 0) return "hoy";
  if (diff === 1) return "mañana";
  const d = new Date(`${iso}T00:00:00Z`);
  return `el ${d.toLocaleDateString("es-ES", { day: "numeric", month: "short", timeZone: "UTC" })}`;
}

// ---------------------------------------------------------------------------
// Llegadas, salidas, preparación y peticiones
// ---------------------------------------------------------------------------

/**
 * Una reserva puede existir sin unidad física asignada todavía (§6.1). Hoy la
 * enseña igual, con un marcador, en vez de romperse.
 */
const UNASSIGNED_UNIT = { villa_id: "—", name: "Sin asignar", bedrooms: 0, sleeps: 0 };

/**
 * Las llegadas de hoy.
 *
 * La hora que se enseña es la de llegada A LA PROPIEDAD, no la de aterrizaje.
 * Son dos hechos distintos y confundirlos tiene consecuencias: los Papadakis
 * aterrizan a las 21:40 y llegan a la villa a las 23:10, así que recepción
 * esperándolos a las 21:40 son hora y media de guardia para nada. El aterrizaje
 * viaja aparte, en `landing_time`, porque también sirve —para saber si el vuelo
 * se ha retrasado—, pero no es la hora a la que hay que abrir la puerta.
 *
 * Todo esto se DERIVA del modelo de huésped, que es donde el huésped lo
 * declara (§7.1). Aquí no se declara ninguna llegada.
 */
function buildArrivals({ iso, state, checkInTime, settings, guestState, requests, reservationsState }) {
  return arrivalsOn(iso).map((r) => {
    const party = buildGuestParty({ reservation: r, settings, storeState: guestState });
    const arrival = party.arrival;
    const mode = arrivalModeOf(arrival?.mode);
    const money = moneyFor({
      reservation: r,
      services: servicesFor(r, requests),
      settings,
      events: paymentEventsFor(r, reservationsState),
    });
    return {
      reservation: r,
      unit: getVilla(r.assigned_villa_id) || UNASSIGNED_UNIT,
      channel: getChannel(r.channel),
      // Hora prevista de llegada A LA PROPIEDAD, y solo si el huésped la ha
      // dicho. Cuando no la ha dicho no hay hora: la de entrada de Ajustes es
      // el horario de la casa, no una llegada que nadie ha anunciado, y
      // enseñarla ahí sería inventarle al huésped una respuesta que no ha
      // dado. Tampoco se deduce del vuelo.
      eta: arrival?.estimated_arrival || null,
      // El aterrizaje o la llegada al puerto, cuando aplica. Nunca es el `eta`.
      landing_time: mode.time_label ? arrival?.landing_time || null : null,
      landing_label: mode.time_label,
      arrival_mode: arrival?.mode || null,
      arrival_mode_label: arrival ? mode.label : null,
      arrival_reference: mode.ref_label ? arrival?.reference || null : null,
      balance_due_eur: money.outstanding_eur,
      transfer: transferStateFor(arrival, r.reservation_id, requests),
      checked_in: Boolean(state.arrivals[r.reservation_id]?.checked_in),
    };
  }).sort((a, b) => arrivalOrder(a, checkInTime).localeCompare(arrivalOrder(b, checkInTime)));
}

/**
 * Dónde va la fila en la lista, y NADA más.
 *
 * A quien no ha dicho hora hay que colocarlo en algún sitio, y el sitio
 * razonable es la hora de entrada de la casa. Pero eso es una decisión de
 * ordenación, no un dato del huésped: por eso se calcula aquí y no viaja en la
 * fila, donde antes o después alguien acabaría pintándolo.
 */
function arrivalOrder(row, checkInTime) {
  return row.eta || checkInTime;
}

/**
 * El traslado, para el equipo.
 *
 * Lo que el huésped pidió sale del modelo de huésped; si además existe un
 * pedido de servicio (§9), de ahí sale cómo va. `null` cuando ni siquiera se
 * le preguntó —quien llega por su cuenta no tiene punto de recogida—, para que
 * recepción no persiga una respuesta que nadie pidió.
 */
function transferStateFor(arrival, reservationId, requests) {
  if (!asksTransfer(arrival) || arrival?.transfer !== "book") return null;
  const order = (requests || []).find((q) => q.reservation_id === reservationId
    && (q.type === "airport_pickup" || q.service_id === "svc_airport"));
  return {
    requested: true,
    confirmed: Boolean(order) && order.status !== "requested",
    scheduled_for: order?.scheduled_for || null,
  };
}

/**
 * Las salidas de hoy.
 *
 * La salida tardía SÍ se declara: es un servicio pedido y resuelto, y no lo
 * modela ningún otro sitio. El saldo, en cambio, sale del mismo cálculo que
 * usa la ficha de la reserva.
 */
function buildDepartures({ iso, checkOutTime, settings, requests, reservationsState }) {
  return departuresOn(iso).map((r) => {
    const late = DEPARTURE_DETAILS[r.reservation_id]?.late_checkout || null;
    const money = moneyFor({
      reservation: r,
      services: servicesFor(r, requests),
      settings,
      events: paymentEventsFor(r, reservationsState),
    });
    return {
      reservation: r,
      unit: getVilla(r.assigned_villa_id) || UNASSIGNED_UNIT,
      checkout_time: late?.status === "granted" && late.until ? late.until : checkOutTime,
      scheduled_checkout: checkOutTime,
      balance_due_eur: money.outstanding_eur,
      late_checkout: late,
    };
  });
}

/**
 * Preparación de las llegadas.
 *
 * NO calcula nada por su cuenta: lo deriva el modelo de huésped
 * (`guestsV2Selectors`), que es donde viven las personas, lo que falta y el
 * reporte de inmigración. Hoy solo lo ordena y lo recorta.
 *
 * Así la fila de Hoy y la ficha de la reserva no pueden contradecirse: leen lo
 * mismo. Antes cada una llevaba su propia cuenta de campos.
 */
function buildReadiness({ arrivals, settings, guestState }) {
  return arrivals
    .map((a) => {
      const view = buildGuestParty({
        reservation: a.reservation, settings, storeState: guestState,
      });
      if (view.guests.length === 0) return null;
      return {
        reservation: a.reservation,
        unit: a.unit,
        party: view.party,
        pre_checkin: view.pre_checkin,
        missing: view.missing,
        immigration: view.immigration.state,
        immigration_tracked: view.immigration.tracked,
        operational_notes: view.operational_notes,
        arrival: view.arrival,
        ready: view.ready,
        view,
      };
    })
    .filter(Boolean)
    .sort((a, b) => Number(a.ready) - Number(b.ready));
}

/**
 * Peticiones del huésped ya resueltas: etiqueta, área, servicio del catálogo y
 * unidad. Se exporta porque la ficha de reserva lee exactamente lo mismo — una
 * petición no puede significar una cosa en Hoy y otra en Reservas.
 */
export function enrichRequests({ state, services, unitLabel }) {
  const byId = Object.fromEntries((services || []).map((s) => [s.service_id, s]));
  return state.requests.map((r) => {
    const type = REQUEST_TYPE_BY_KEY[r.type] || {};
    // Un pedido creado desde la ficha de reserva referencia directamente un
    // servicio del catálogo en vez de uno de los tipos de un toque del portal.
    const serviceId = type.service_id || r.service_id || null;
    const service = serviceId ? byId[serviceId] : null;
    const reservation = getReservation(r.reservation_id);
    return {
      ...r,
      guest_name: reservation?.guest?.name || null,
      label: type.label || type.label_template
        ? requestLabel(r.type, unitLabel)
        : (service?.name || "Servicio"),
      area: type.area || "services",
      creates: type.creates || "service_order",
      icon: type.icon || "Basket",
      free_text: Boolean(type.free_text),
      unit: getVilla(r.villa_id),
      service,
      // Un servicio desactivado en Ajustes deja de ofrecerse: lo ya pedido se
      // sigue atendiendo, pero conviene que se vea.
      service_inactive: Boolean(service && service.active === false),
    };
  });
}

// ---------------------------------------------------------------------------
// Qué enseña Hoy de cada lista (divulgación progresiva)
// ---------------------------------------------------------------------------

/**
 * Limpieza que Hoy debe enseñar: las excepciones, no el módulo entero. Entra
 * la salida con llegada detrás, lo que espera una llegada sin estar lista y lo
 * que ha pedido un huésped. La limpieza rutinaria vive en Housekeeping.
 */
export function housekeepingNeedingAttention(tasks) {
  return tasks.filter((t) => t.same_day
    || Boolean(t.from_request)
    || Boolean(t.next_arrival && !HK_DONE.has(t.status)));
}

const ISSUE_PRIORITY_RANK = { urgent: 0, high: 1, medium: 2, low: 3 };

/** Primero lo que impide vender la unidad; después, por urgencia y antigüedad. */
export function sortIssuesByImpact(issues) {
  return [...issues].sort((a, b) => {
    const blocking = Number(Boolean(b.blocks_inventory)) - Number(Boolean(a.blocks_inventory));
    if (blocking !== 0) return blocking;
    const pa = ISSUE_PRIORITY_RANK[a.priority] ?? 9;
    const pb = ISSUE_PRIORITY_RANK[b.priority] ?? 9;
    if (pa !== pb) return pa - pb;
    return String(a.reported_at).localeCompare(String(b.reported_at));
  });
}

const REQUEST_RANK = { requested: 0, in_progress: 1, confirmed: 2, delivered: 3, cancelled: 4 };

/** Lo que nadie ha cogido va primero; luego lo urgente, luego lo demás. */
export function sortRequestsByUrgency(requests) {
  return [...requests].sort((a, b) => {
    const ra = REQUEST_RANK[a.status] ?? 9;
    const rb = REQUEST_RANK[b.status] ?? 9;
    if (ra !== rb) return ra - rb;
    const ua = a.urgency === "urgent" || a.urgency === "high" ? 0 : 1;
    const ub = b.urgency === "urgent" || b.urgency === "high" ? 0 : 1;
    if (ua !== ub) return ua - ub;
    return String(a.request_id).localeCompare(String(b.request_id));
  });
}

/**
 * Una sola frase por huésped. La lista de campos solo aparece si se abre la
 * fila: en Hoy interesa saber quién no está listo, no repasar su formulario.
 */
export function readinessSummary(row) {
  if (row.view) return readinessLine(row.view);
  // Fila ya aplanada: se normaliza a la forma del modelo antes de resumirla.
  // Sin esto, `immigration` llegaría como cadena donde se espera un objeto y la
  // parte de inmigración desaparecería de la frase sin avisar.
  return readinessLine({
    ready: row.ready,
    missing: row.missing || [],
    immigration: { tracked: Boolean(row.immigration_tracked), state: row.immigration },
  });
}

// ---------------------------------------------------------------------------
// Avisos — representación de demo del futuro Action Center
// ---------------------------------------------------------------------------

/**
 * Mismo criterio que `_dedup_actions` en `backend/actions_engine.py`: dos
 * señales sobre la misma `(entity_type, entity_id)` colapsan en la más
 * urgente. Se replica aquí para que la maqueta no enseñe un comportamiento
 * que el motor real no tiene.
 */
export function dedupeAttention(items) {
  const byKey = new Map();
  for (const item of items) {
    const key = `${item.entity_type}:${item.entity_id}`;
    const current = byKey.get(key);
    if (!current
      || URGENCY_ORDER.indexOf(item.urgency) < URGENCY_ORDER.indexOf(current.urgency)) {
      byKey.set(key, item);
    }
  }
  return [...byKey.values()].sort((a, b) => {
    const ua = URGENCY_ORDER.indexOf(a.urgency);
    const ub = URGENCY_ORDER.indexOf(b.urgency);
    if (ua !== ub) return ua - ub;
    return String(a.action_id).localeCompare(String(b.action_id));
  });
}

/**
 * Reglas de la maqueta. CADA UNA corresponde a una señal que en el producto
 * real emitirá su módulo hacia el Action Center (§16.1), nunca esta pantalla:
 *
 *   R1 unidad fuera de servicio con incidencia bloqueante   → maintenance
 *   R2 llegada hoy con la limpieza sin terminar             → housekeeping
 *   R3 llegada hoy con el check-in online incompleto        → reservation
 *   R4 reportes de inmigración pendientes                   → guest
 *   R5 traslado de llegada sin confirmar                    → service
 *   R6 petición del huésped sin atender                     → housekeeping | service
 *
 * R4 se agrega en una sola fila a propósito: en un móvil, tres avisos
 * idénticos son ruido. El Action Center real emitiría uno por reserva y los
 * agruparía por tipo.
 */
function buildAttention({ board, arrivals, housekeeping, readiness, requests, unitLabel }) {
  const items = [];
  const unitWord = unitLabel.singular;

  // R1 — la unidad no se puede vender.
  for (const row of board) {
    if (row.state !== "OUT_OF_SERVICE") continue;
    items.push({
      action_id: `demo:maintenance:${row.unit.villa_id}`,
      source: "maintenance",
      entity_type: "unit",
      entity_id: row.unit.villa_id,
      urgency: "CRITICAL",
      unit_id: row.unit.villa_id,
      title: `${row.unit.villa_id} · ${row.issue ? row.issue.title : "Fuera de servicio"}`,
      reason: `${unitWord} fuera de servicio · disponibilidad bloqueada hasta el ${row.block.end_date.slice(8)}`,
      meta: row.issue?.assignee ? `Asignada a ${row.issue.assignee.name}` : "Sin asignar",
      cta: { label: "Abrir incidencias", to: "/pms/issues" },
    });
  }

  // R2 — llega alguien y la unidad no está lista.
  for (const task of housekeeping) {
    if (!task.next_arrival || HK_DONE.has(task.status)) continue;
    const arrival = arrivals.find(
      (a) => a.reservation.reservation_id === task.next_arrival.reservation_id,
    );
    items.push({
      action_id: `demo:housekeeping:${task.unit_id}`,
      source: "housekeeping",
      entity_type: "unit",
      entity_id: task.unit_id,
      urgency: "CRITICAL",
      unit_id: task.unit_id,
      title: `${task.unit_id} · limpieza sin terminar`,
      // Sin hora declarada el aviso dice que llega, no a qué hora: la hora de
      // entrada de Ajustes no es la respuesta del huésped.
      reason: task.same_day
        ? `Salida y llegada el mismo día${arrival?.eta ? ` · el huésped llega a las ${arrival.eta}` : ""}`
        : `${arrival?.eta ? `Llegada a las ${arrival.eta}` : "Llega hoy"} · limpieza ${task.status === "PENDING" ? "sin empezar" : "en curso"}`,
      meta: task.assignee ? `A cargo de ${task.assignee}` : "Sin asignar",
      cta: { label: "Ver limpieza", section: "housekeeping" },
    });
  }

  // R3 — el huésped llega sin haber terminado el check-in online.
  for (const row of readiness) {
    if (row.pre_checkin === "completed" && row.missing.length === 0) continue;
    items.push({
      action_id: `demo:reservation:${row.reservation.reservation_id}`,
      source: "reservation",
      entity_type: "reservation",
      entity_id: row.reservation.reservation_id,
      urgency: "TODAY",
      unit_id: row.unit.villa_id,
      title: `${row.reservation.guest.name} · check-in online a medias`,
      reason: row.missing.length
        ? `${row.unit.villa_id} · falta ${row.missing.length === 1 ? "1 dato" : `${row.missing.length} datos`} del huésped`
        : `${row.unit.villa_id} · el huésped no ha terminado el check-in`,
      meta: "Llega hoy",
      cta: { label: "Abrir reserva", to: `/pms/reservations/${row.reservation.reservation_id}` },
    });
  }

  // R4 — cola de inmigración (agregada).
  const immigrationDue = readiness.filter((r) => r.immigration_tracked && r.immigration === "due");
  if (immigrationDue.length > 0) {
    items.push({
      action_id: "demo:guest:immigration",
      source: "guest",
      entity_type: "immigration_queue",
      entity_id: "today",
      urgency: "TODAY",
      unit_id: null,
      title: `${immigrationDue.length} reportes de inmigración pendientes`,
      reason: `${immigrationDue.map((r) => r.unit.villa_id).join(" · ")} · el reporte lo presenta la propiedad`,
      meta: "Sin integración con ningún sistema oficial",
      cta: { label: "Ver preparación", section: "readiness" },
    });
  }

  // R5 — traslado de llegada sin confirmar con el proveedor.
  //
  // El traslado lo pide el huésped en su pre-check-in y lo confirma el pedido
  // de servicio (§9): las dos mitades del mismo hecho, y ninguna de ellas es
  // ya la copia que se borró. Mientras el pedido siga sin coger, recepción
  // tiene algo que hacer.
  for (const arrival of arrivals) {
    const t = arrival.transfer;
    if (!t || !t.requested || t.confirmed) continue;
    const request = requests.find(
      (r) => r.reservation_id === arrival.reservation.reservation_id && r.type === "airport_pickup",
    );
    items.push({
      action_id: `demo:service:${request?.request_id || arrival.reservation.reservation_id}`,
      source: "service",
      entity_type: "request",
      entity_id: request?.request_id || arrival.reservation.reservation_id,
      urgency: "TODAY",
      unit_id: arrival.unit.villa_id,
      title: `Traslado sin confirmar · ${arrival.unit.villa_id}`,
      reason: [
        t.scheduled_for ? `Recogida a las ${t.scheduled_for}` : "Recogida sin hora",
        arrival.arrival_reference
          ? `${arrivalModeOf(arrival.arrival_mode).ref_label?.toLowerCase()} ${arrival.arrival_reference}`
          : null,
        arrival.eta ? `llegada a las ${arrival.eta}` : null,
      ].filter(Boolean).join(" · "),
      meta: arrival.reservation.guest.name,
      cta: { label: "Ver peticiones", section: "requests" },
    });
  }

  // R6 — peticiones que nadie ha cogido todavía.
  for (const request of requests) {
    if (request.status !== "requested") continue;
    items.push({
      action_id: `demo:request:${request.request_id}`,
      source: request.area === "housekeeping" ? "housekeeping" : "service",
      entity_type: "request",
      entity_id: request.request_id,
      urgency: "TODAY",
      unit_id: request.villa_id,
      title: `${request.label} · ${request.villa_id}`,
      reason: `Petición del huésped sin atender desde las ${request.requested_at}`,
      meta: request.scheduled_for ? `Para ${request.scheduled_for}` : "Sin hora asignada",
      cta: { label: "Ver peticiones", section: "requests" },
    });
  }

  return dedupeAttention(items);
}

// ---------------------------------------------------------------------------
// Composición
// ---------------------------------------------------------------------------

/**
 * Punto de entrada único de la pantalla. Recibe el estado de la vista previa,
 * los ajustes (vocabulario, horarios, servicios, requisitos) y las incidencias
 * del store PMS, y devuelve todo lo que Hoy pinta.
 */
export function buildTodayV2({
  state, settings, issues, unitLabel, guestState, reservationsState, iso = DEMO_TODAY_ISO,
}) {
  const checkInTime = settings.property.check_in_time;
  const checkOutTime = settings.property.check_out_time;

  const housekeeping = buildHousekeeping({ iso, state, unitLabel });
  const openIssues = issues
    .filter((i) => i.status !== "resolved")
    .map((i) => ({ ...i, status_v2: issueStatusV2(i), unit: getVilla(i.villa_id) }))
    .sort((a, b) => Number(b.blocks_inventory) - Number(a.blocks_inventory));

  const requests = enrichRequests({ state, services: settings.services, unitLabel });
  const arrivals = buildArrivals({
    iso, state, checkInTime, settings, guestState, requests, reservationsState,
  });
  const departures = buildDepartures({
    iso, checkOutTime, settings, requests, reservationsState,
  });
  const readiness = buildReadiness({ arrivals, settings, guestState });
  const etaByReservation = Object.fromEntries(
    arrivals.map((a) => [a.reservation.reservation_id, a.eta]),
  );
  const board = buildBoard({ iso, housekeeping, issues, checkOutTime, etaByReservation });

  const attention = buildAttention({
    board, arrivals, housekeeping, readiness, requests, unitLabel,
  });

  const occupiedTonight = board.filter((row) => row.inStay).length;
  const pendingCleaning = housekeeping.filter((t) => !HK_DONE.has(t.status)).length;
  const blockingIssues = openIssues.filter((i) => i.blocks_inventory).length;

  return {
    iso,
    dateLabel: formatTodayLabel(iso),
    checkInTime,
    checkOutTime,
    arrivals,
    departures,
    housekeeping,
    issues: openIssues,
    readiness,
    requests,
    board,
    attention,
    snapshot: {
      arrivals: arrivals.length,
      departures: departures.length,
      cleaningPending: pendingCleaning,
      cleaningTotal: housekeeping.length,
      issues: openIssues.length,
      blockingIssues,
      occupiedTonight,
      units: board.length,
    },
  };
}

/** Configuración de la vista previa de rol, con reserva segura. */
export function rolePreviewFor(roleKey) {
  return ROLE_PREVIEW_BY_KEY[roleKey] || ROLE_PREVIEW_BY_KEY[DEFAULT_ROLE_PREVIEW];
}

/** Avisos que vería ese rol. `attention_sources: null` significa "todo". */
export function attentionForRole(attention, role) {
  if (!role.attention_sources) return attention;
  return attention.filter((a) => role.attention_sources.includes(a.source));
}
